import React, { useState } from 'react';
import moment from 'moment-timezone';
import type {
  CalendarScheduledInterview,
  ClientRequest,
  Opportunity,
  TimeZone,
} from '@axiom/validation';
import type { CleanEvent } from '@axiom/ui';
import {
  Badge,
  Banner,
  Button,
  CalendarTimeZoneUtil,
  DateUtil,
  Flashy,
  Form,
  Gutter,
  Icon,
  Layout,
  LayoutItem,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  Paragraph,
  SmallHeader,
  useOnDependencyUpdate,
  BadRequestError,
  useApi,
} from '@axiom/ui';

import { AccountsApi } from '../../api/accounts-api';
import { CalendarApi } from '../../api/calendar-api';
import type { InterviewCandidateType } from '../../hooks/useInterviewAvailabilityCandidateData';
import { InterviewAttendeesAndLinkModalSchema } from '../../schema/interview-attendees-and-link-modal-schema';
import { InterviewAttendeesAndLinkModalFormBody } from '../InterviewAttendeesAndLinkModalFormBody/InterviewAttendeesAndLinkModalFormBody';

export type InterviewAttendeesAndLinkModalCreateProps = {
  candidate: InterviewCandidateType;
  clientRequestId?: ClientRequest['id'];
  onBackHandler: () => void;
  onClose: (addDelay?: boolean) => void;
  opportunity: Opportunity;
  refreshData: () => Promise<void>;
  scheduledEvents: CleanEvent[];
  timezone: TimeZone;
};

export const InterviewAttendeesAndLinkModalCreate = ({
  candidate,
  clientRequestId,
  onBackHandler,
  onClose,
  opportunity,
  refreshData,
  scheduledEvents,
  timezone,
}: InterviewAttendeesAndLinkModalCreateProps) => {
  if (!opportunity.accountId) {
    throw new Error('No account found');
  }
  const { accountId } = opportunity;

  const [{ data: account }] = useApi(AccountsApi.readAccount(accountId));
  const accountContacts = account.contacts;
  const [currentEventIndex, setCurrentEventIndex] = useState(0);
  const [currentEvent, setCurrentEvent] = useState<CleanEvent | null>(
    scheduledEvents[currentEventIndex] ?? null
  );
  const [isInterviewTimeUnavailable, setIsInterviewTimeUnavailable] =
    useState(false);

  useOnDependencyUpdate(() => {
    setCurrentEvent(scheduledEvents[currentEventIndex] ?? null);
  }, [currentEventIndex]);

  const initialValues = {
    contacts: accountContacts[0]?.id ? [accountContacts[0].id] : [],
    adHocContacts: [] as Array<{
      firstName: string;
      lastName: string;
      email: string;
      readonly: boolean;
    }>,
    meetingLinkType: 'ZOOM' as const,
    meetingLink: '',
  };

  const isLastEvent = currentEventIndex >= scheduledEvents.length - 1;

  // Re-resolve abbr/name against this event's actual startTime so the label
  // reflects the DST in effect at the interview moment, not at modal-open
  // time. The prop's iana is still the source of truth for time formatting.
  const displayTimezone =
    (currentEvent?.startTime
      ? CalendarTimeZoneUtil.getZoneFromMomentZone(
          moment.tz(currentEvent.startTime, timezone.iana)
        )
      : null) ?? timezone;

  return (
    <Form
      name="CREATE_ATTENDEES_FORM"
      schema={InterviewAttendeesAndLinkModalSchema}
      initialValues={initialValues}
      onSubmit={async (_changedValues, _actions, values) => {
        if (!currentEvent) return;

        const { title, startTime, endTime, busy } = currentEvent;

        const payload: CalendarScheduledInterview & {
          busy?: boolean;
          preferredVideoClient?: string | null;
          clientRequestId?: ClientRequest['id'];
          meetingLink?: string;
        } = {
          adhocAttendees: values.adHocContacts.filter(c => c.email),
          contactIds: values.contacts,
          candidateId: candidate.id,
          submissionId: opportunity.submissionId,
          title,
          startTime,
          endTime,
          busy,
          preferredVideoClient:
            values.meetingLinkType === 'ZOOM' ? 'Zoom' : null,
          meetingLink:
            values.meetingLinkType === 'MANUAL'
              ? (values.meetingLink ?? undefined)
              : undefined,
          ...(clientRequestId && currentEventIndex === 0
            ? { clientRequestId }
            : {}),
        };

        try {
          await CalendarApi.createScheduledCalendarInterview(payload);
          setIsInterviewTimeUnavailable(false);
        } catch (e) {
          if (e instanceof BadRequestError) {
            setIsInterviewTimeUnavailable(true);
          }
          return;
        }

        if (!isLastEvent) {
          setCurrentEventIndex(currentEventIndex + 1);
          return;
        }

        onClose(true);
        await refreshData();
      }}
    >
      {({ values, setValues, fireSubmit }) => (
        <Modal name="CREATE_ATTENDEES_MODAL" size="large">
          <ModalHeader
            name="CREATE_ATTENDEES_MODAL_HEADER"
            onClose={() => {
              onClose();
            }}
          >
            <Layout position="middle" wrap>
              <LayoutItem fluid>
                <SmallHeader name="HEADER_TEXT">
                  Attendees and meeting link for the interview with{' '}
                  {candidate.displayName}
                </SmallHeader>
              </LayoutItem>
              <LayoutItem>
                <Badge
                  name="SCHEDULE_INTERVIEW_BADGE"
                  background="forest green"
                >
                  Schedule interview
                </Badge>
              </LayoutItem>
            </Layout>
          </ModalHeader>
          <ModalSection>
            <Gutter bottom="16px">
              {isInterviewTimeUnavailable ? (
                <Banner
                  name="INTERVIEW_UNAVAILABLE_BANNER"
                  type="error"
                  impact="high"
                >
                  <Layout position="middle" horizontalGutter="8px">
                    <Paragraph>
                      <Flashy color="textAlert">
                        <Icon name="alert-filled" />
                      </Flashy>
                    </Paragraph>
                    <Paragraph>
                      The selected interview time is no longer available. Please
                      try again.
                    </Paragraph>
                  </Layout>
                </Banner>
              ) : (
                <Banner name="INTERVIEW_BANNER">
                  <Flashy bold color="controlSecondary">
                    Interview{' '}
                    {scheduledEvents.length > 1
                      ? `(${currentEventIndex + 1} of ${scheduledEvents.length}) `
                      : ' '}
                  </Flashy>
                  {DateUtil.displayBannerTimestamp(
                    currentEvent?.startTime,
                    currentEvent?.endTime,
                    displayTimezone
                  )}{' '}
                  {displayTimezone.abbr} {displayTimezone.name}
                </Banner>
              )}
            </Gutter>
            <InterviewAttendeesAndLinkModalFormBody
              accountContacts={accountContacts}
              accountId={accountId}
              talentName={candidate.displayName}
              values={values}
              setValues={setValues}
            />
          </ModalSection>
          <ModalFooter>
            <Button
              name="BACK_BUTTON"
              onClick={() => {
                onBackHandler();
              }}
              pattern="primary"
              variation="outline"
            >
              Back
            </Button>
            <Button name="NEXT_BUTTON" onClick={fireSubmit}>
              {isLastEvent ? 'Schedule Interview' : 'Next'}
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </Form>
  );
};
